import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { Avatar } from '../atoms'
import { ReportMap } from './ReportMap'
import { ReportSection, ReportPin } from './reportChrome'
// The shipped downed-game editor, reused as a FORM only (hideList): the card owns
// the list, counts, preview map and modal in its own design, and drives this
// component's add/edit form from the outside. One editor, one set of permission
// rules — never a second copy that would drift from the tab's.
import { DownedGameTab, buildParticipantPool, type GamePinDraft } from '../HuntInvitationModal'
import {
  countGamePins,
  formatPinTime,
  reporterInitials,
  speciesLabel,
  sortPinsByTime,
  ROW_TYPE_LABEL_KEY,
  type GameCounts,
} from './gameReports'
import type { HuntableGameSpecies } from '../../lib/huntable-game-catalog'
import type {
  GamePinInput,
  GamePinView,
  GeoArea,
  GeoPt,
  HuntPlanParticipant,
} from '../../lib/types'

/** Height of the collapsed-card preview map, in px. Tall enough to read the
 *  ground under the pins, short enough that the list stays above the fold. */
const PREVIEW_MAP_HEIGHT = 230

export interface GameReportsCardProps {
  gamePins: GamePinView[]
  participants: HuntPlanParticipant[]
  speciesCatalog?: readonly HuntableGameSpecies[]
  /** The signed-in user, so their own pins can be edited without manage-all. */
  currentUserId?: string
  /** True when the viewer may edit anyone's game report, not only their own. */
  canManageAll?: boolean
  /** Where a newly added pin is placed when the user does not move it. */
  defaultLocation?: GeoPt
  /** The pins could not be read — passed to the editor for its own message. */
  gamePinsLoadFailed?: boolean
  /** The team ground the maps draw under the pins. */
  boundaryAreas?: readonly GeoArea[]
  subAreaPolygons?: readonly GeoArea[]
  forbiddenPolygons?: readonly GeoArea[]
  teamBorderColor?: string
  /**
   * The selected pin. Optional: the card manages its own selection state when
   * left uncontrolled, so a row click drives the preview map and the modal on
   * its own. Pass both to lift the selection into a parent instead.
   */
  selectedPinId?: string | null
  onSelectPin?: (pinId: string | null) => void
  /**
   * Forwarded to the editor: it reports the open add/edit form's position intent
   * (pinType + seed camera) so a host that owns a map — the report page's big
   * left map — can host the centre-pin picker. Null when the form closes.
   */
  onDraftChange?: (draft: GamePinDraft | null) => void
  /** The location picked on that host map, forwarded to the editor so submit
   *  writes the pin there rather than at the ground centroid. */
  draftLocation?: GeoPt | null
  /**
   * Passed straight through to the editor. Omitting `onCreateGamePin` renders the
   * card read-only (no Add affordance), which is what a viewer without permission
   * should see.
   */
  onCreateGamePin?: (input: GamePinInput) => Promise<void>
  onUpdateGamePin?: (id: string, input: GamePinInput) => Promise<void>
  onDeleteGamePin?: (id: string) => Promise<void>
}

/**
 * The ended hunt's game-reports card: a collapsible section with a preview map
 * + a floating counts pill, a "View all on map" button that
 * opens a full-screen map, a list of every reported pin, and — when the viewer
 * may add game — an Add button. Add / edit / remove reuse the shipped
 * `DownedGameTab` as a form-only editor; the card controls which form is open.
 *
 * Observed, downed and wounded are kept distinct throughout: the earlier report
 * folded anything not downed into "wounded", which reported a sighting as an
 * outstanding tracking job.
 */
export function GameReportsCard({
  gamePins,
  participants,
  speciesCatalog = [],
  currentUserId,
  canManageAll = false,
  defaultLocation,
  gamePinsLoadFailed = false,
  boundaryAreas,
  subAreaPolygons,
  forbiddenPolygons,
  teamBorderColor,
  selectedPinId: selectedPinIdProp,
  onSelectPin: onSelectPinProp,
  onDraftChange,
  draftLocation,
  onCreateGamePin,
  onUpdateGamePin,
  onDeleteGamePin,
}: GameReportsCardProps) {
  const { t, i18n } = useTranslation('huntInvitation')
  const counts = countGamePins(gamePins)
  const sortedPins = sortPinsByTime(gamePins)
  const [mapOpen, setMapOpen] = useState(false)
  // Selection is controlled when the parent passes `selectedPinId`, otherwise the
  // card owns it — a row click still drives the preview map and the modal. Both
  // surfaces read the same value, so a selection highlights everywhere at once.
  const [selfSelectedPinId, setSelfSelectedPinId] = useState<string | null>(null)
  const controlled = selectedPinIdProp !== undefined
  const selectedPinId = controlled ? selectedPinIdProp : selfSelectedPinId
  const onSelectPin = useCallback(
    (pinId: string | null) => {
      if (!controlled) setSelfSelectedPinId(pinId)
      onSelectPinProp?.(pinId)
    },
    [controlled, onSelectPinProp],
  )
  // Which editor form is open: null = none, 'new' = add, or a pin id = edit.
  // Owned here so the card's own Add / row-edit controls drive the reused tab.
  const [openForm, setOpenForm] = useState<string | null>(null)

  const canEditPin = (pin: GamePinView) =>
    !!onUpdateGamePin && (canManageAll || pin.reporterId === currentUserId)

  const openAdd = () => {
    setMapOpen(false)
    setOpenForm('new')
  }

  return (
    <ReportSection title={t('gameReports.title')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Preview map with the counts pill and the "View all" button floating
            over it. Wrapped in a relative box because ReportMap has no overlay
            slot of its own. */}
        <div style={{ position: 'relative' }}>
          <ReportMap
            gamePins={gamePins}
            boundaryAreas={boundaryAreas}
            subAreaPolygons={subAreaPolygons}
            forbiddenPolygons={forbiddenPolygons}
            teamBorderColor={teamBorderColor}
            height={PREVIEW_MAP_HEIGHT}
            selectedPinId={selectedPinId}
            onSelectPin={onSelectPin}
          />
          <CountsPill counts={counts} />
          <button
            type="button"
            className="wh-btn wh-btn-primary wh-btn-sm"
            onClick={() => setMapOpen(true)}
            style={{ position: 'absolute', right: 10, bottom: 10 }}
          >
            <Icon name="map" size={16} />
            {t('gameReports.viewAllOnMap')}
          </button>
        </div>

        {/* The pin list, time-ordered. Observed / downed / wounded each keep their
            own pin art and their own type label — wounded stays a first-class row
            here, never collapsed away. */}
        {sortedPins.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {sortedPins.map((pin) => {
              const time = formatPinTime(pin.date, i18n.language)
              const typeLabel = t(ROW_TYPE_LABEL_KEY[pin.pinType])
              const isSelected = selectedPinId === pin.id
              return (
                // The whole row navigates the maps to this game. A div with a
                // button role rather than a <button>, because it contains its own
                // edit / locate buttons and a <button> may not nest interactive
                // children. Enter and Space activate it, matching a real button.
                <div
                  key={pin.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-label={t('gameReports.showOnMap', { species: speciesLabel(pin, speciesCatalog) })}
                  onClick={() => onSelectPin(pin.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectPin(pin.id)
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 8px',
                    borderTop: `1px solid ${COLORS.outlineVariant}`,
                    cursor: 'pointer',
                    borderRadius: 8,
                    background: isSelected ? COLORS.primaryOpacity12 : undefined,
                  }}
                >
                  <ReportPin kind={pin.pinType} size={22} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{speciesLabel(pin, speciesCatalog)}</div>
                    <div style={{ fontSize: 12, color: COLORS.outline }}>
                      {time ? `${typeLabel} ${time}` : typeLabel}
                    </div>
                    {/* The reporter's note, shown in the read list rather than only
                        inside the editor — a comment is the whole point of some
                        reports ("wounded, blood trail north"). Truncated to one
                        line so a long note can't break the row's layout; the full
                        text stays available on hover. */}
                    {pin.comment && pin.comment.trim() !== '' ? (
                      <div
                        title={pin.comment}
                        style={{
                          fontSize: 12,
                          color: COLORS.outline,
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontStyle: 'italic',
                        }}
                      >
                        {pin.comment}
                      </div>
                    ) : null}
                  </div>
                  <Avatar initials={reporterInitials(pin, participants)} size={30} />
                  {canEditPin(pin) ? (
                    <button
                      type="button"
                      className="wh-btn-icon"
                      title={t('downedGame.edit')}
                      onClick={(e) => {
                        // Don't let the row's own click also fire a select.
                        e.stopPropagation()
                        setMapOpen(false)
                        setOpenForm(pin.id)
                      }}
                      style={{ width: 32, height: 32 }}
                    >
                      <Icon name="edit" size={16} />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="wh-btn-icon"
                    title={t('report.mapPinLabel')}
                    aria-label={t('report.mapPinLabel')}
                    aria-pressed={isSelected}
                    onClick={(e) => {
                      // The dedicated locate button toggles; the row itself only
                      // selects. Stop the row's click so the two don't fight.
                      e.stopPropagation()
                      onSelectPin(isSelected ? null : pin.id)
                    }}
                    style={{ width: 32, height: 32 }}
                  >
                    <Icon name="pin" size={18} />
                  </button>
                </div>
              )
            })}
          </div>
        ) : null}

        {/* Add game — hidden entirely when the viewer may not create a pin. */}
        {onCreateGamePin && openForm === null ? (
          <div>
            <button type="button" className="wh-btn wh-btn-outline" onClick={openAdd}>
              <Icon name="plus" size={16} />
              {t('gameReports.addGame')}
            </button>
          </div>
        ) : null}

        {/* The reused editor, form-only: it renders the open add/edit form and its
            delete confirmation, and nothing else. The card owns the list above. */}
        <DownedGameTab
          hideList
          gamePins={gamePins}
          loadFailed={gamePinsLoadFailed}
          participantPool={buildParticipantPool(participants)}
          currentUserId={currentUserId}
          canManageAll={canManageAll}
          defaultLocation={defaultLocation}
          speciesCatalog={speciesCatalog}
          openForm={openForm}
          onOpenFormChange={setOpenForm}
          onDraftChange={onDraftChange}
          draftLocation={draftLocation}
          onCreateGamePin={onCreateGamePin}
          onUpdateGamePin={onUpdateGamePin}
          onDeleteGamePin={onDeleteGamePin}
        />
      </div>

      {mapOpen ? (
        <GameReportsMapModal
          gamePins={gamePins}
          counts={counts}
          boundaryAreas={boundaryAreas}
          subAreaPolygons={subAreaPolygons}
          forbiddenPolygons={forbiddenPolygons}
          teamBorderColor={teamBorderColor}
          selectedPinId={selectedPinId}
          onSelectPin={onSelectPin}
          onAddGame={onCreateGamePin ? openAdd : undefined}
          onClose={() => setMapOpen(false)}
        />
      ) : null}
    </ReportSection>
  )
}

/** The floating white counts pill. Shows observed, downed AND wounded — wounded
 *  is a real outcome the hunter must not lose sight of, so it gets its own count
 *  here as well as its own rows in the list. */
function CountsPill({ counts }: { counts: GameCounts }) {
  const { t } = useTranslation('huntInvitation')
  const items: { kind: 'observed' | 'downed' | 'wounded'; label: string; value: number }[] = [
    { kind: 'observed', label: t('gameReports.observed'), value: counts.observed },
    { kind: 'downed', label: t('gameReports.downed'), value: counts.downed },
    { kind: 'wounded', label: t('gameReports.wounded'), value: counts.wounded },
  ]
  return (
    <div
      style={{
        position: 'absolute',
        left: 10,
        top: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: '#fff',
        color: '#1a1a1a',
        borderRadius: 999,
        padding: '6px 14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        fontSize: 13,
      }}
    >
      {items.map((item) => (
        <span key={item.kind} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <ReportPin kind={item.kind} size={16} />
          <b>{item.value}</b>
          <span>{item.label}</span>
        </span>
      ))}
    </div>
  )
}

/**
 * The full-screen map of every game report — the "View all on map" destination.
 * A large `ReportMap`, the same counts pill, and (when the viewer may add game)
 * a floating Add button. Scrim + Escape + backdrop click all close it, matching
 * ConfirmModal.
 *
 * Per-hunter position markers are deliberately omitted: web has no per-hunter
 * live-position data for an ended hunt (that lives in the replay archive, which
 * the "Replay hunt" header action already opens), so there is nothing to draw.
 */
function GameReportsMapModal({
  gamePins,
  counts,
  boundaryAreas,
  subAreaPolygons,
  forbiddenPolygons,
  teamBorderColor,
  selectedPinId = null,
  onSelectPin,
  onAddGame,
  onClose,
}: {
  gamePins: GamePinView[]
  counts: GameCounts
  boundaryAreas?: readonly GeoArea[]
  subAreaPolygons?: readonly GeoArea[]
  forbiddenPolygons?: readonly GeoArea[]
  teamBorderColor?: string
  selectedPinId?: string | null
  onSelectPin?: (pinId: string | null) => void
  onAddGame?: () => void
  onClose: () => void
}) {
  const { t } = useTranslation('huntInvitation')
  const titleId = useId()
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: COLORS.surface,
          borderRadius: 12,
          width: 'min(1100px, 100%)',
          height: 'min(760px, 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          <h2 id={titleId} style={{ margin: 0, fontSize: 16, fontWeight: 700, flex: 1 }}>
            {t('gameReports.title')}
          </h2>
          <button type="button" className="wh-btn-icon" title={t('close')} onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </div>
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <ReportMap
            gamePins={gamePins}
            boundaryAreas={boundaryAreas}
            subAreaPolygons={subAreaPolygons}
            forbiddenPolygons={forbiddenPolygons}
            teamBorderColor={teamBorderColor}
            height="100%"
            selectedPinId={selectedPinId}
            onSelectPin={onSelectPin}
          />
          <CountsPill counts={counts} />
          {onAddGame ? (
            <button
              type="button"
              className="wh-btn wh-btn-primary"
              onClick={onAddGame}
              style={{ position: 'absolute', right: 14, bottom: 14 }}
            >
              <Icon name="plus" size={16} />
              {t('gameReports.addGame')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
